import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import {
    Sparkles, ScanLine, Languages, FileText, Zap, Shield, Brain,
    Layout, Download, Edit3, ArrowRight, Check
} from "lucide-react";

const FEATURES = [
    { icon: ScanLine, title: "OCR-Dokumentenerkennung", desc: "Lade Zeugnisse, Lebensläufe und Zertifikate hoch — die KI extrahiert automatisch persönliche Daten, Berufserfahrung, Ausbildung, Zertifikate und Skills." },
    { icon: Brain, title: "KI-Anschreiben", desc: "Personalisierte Anschreiben in Sekunden, perfekt zugeschnitten auf jede Stellenbeschreibung. ATS-optimiert, mit konkreten Bezügen zu deinem Profil." },
    { icon: FileText, title: "Lebenslauf-Generator", desc: "Strukturierte Lebensläufe mit aktiven Bullet-Points, optimierten Keywords und mehreren Premium-Templates (Modern, Business, Kreativ, Executive)." },
    { icon: Languages, title: "9 Sprachen", desc: "Deutsch, Englisch, Türkisch, Französisch, Spanisch, Arabisch, Russisch, Hindi, Albanisch — direkt in der KI integriert." },
    { icon: Layout, title: "Premium-Templates", desc: "Modern, Minimalistisch, Business, Kreativ, Executive — wähle das Layout, das zu dir passt." },
    { icon: Download, title: "PDF & DOCX Export", desc: "Direkter Export deiner Bewerbung als professionelles PDF oder editierbare DOCX-Datei." },
    { icon: Edit3, title: "Volle Bearbeitung", desc: "Jede generierte Bewerbung ist editierbar. Feinschliff vor dem Senden — du behältst die Kontrolle." },
    { icon: Zap, title: "Stellenanalyse", desc: "Füge eine Stellenausschreibung ein — die KI erkennt Anforderungen, Skills und Schlüsselbegriffe." },
    { icon: Shield, title: "DSGVO-konform", desc: "Made in Switzerland, SSL-verschlüsselt. Deine Daten gehören dir — jederzeit löschbar." },
];

export default function FeaturesPage() {
    return (
        <PublicLayout>
            <Seo
                title="Anschreiben & Lebenslauf mit KI erstellen, ATS-optimiert | Bewerbung24.ai"
                description="Alle Funktionen im Überblick: OCR-Dokumentenerkennung, KI-Anschreiben, Lebenslauf-Generator, ATS-Score, 9 Sprachen, Premium-Templates und PDF/DOCX-Export."
                path="/funktionen"
            />
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-radial-fade" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-12">
                    <div className="max-w-3xl">
                        <div className="h-1.5 w-16 rounded-full mb-7" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} />
                        <span className="eyebrow-warm">Funktionen</span>
                        <h1 className="font-heading mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-bold tracking-tighter text-warm-strong leading-[0.95]">
                            Alles was du brauchst —<br />
                            <span className="gradient-warm">in einer Plattform</span>.
                        </h1>
                        <p className="mt-7 text-lg lg:text-xl text-warm max-w-2xl leading-relaxed">
                            Von der OCR über die KI-Generierung bis zum perfekt formatierten PDF: Bewerbung24.ai deckt den gesamten Bewerbungsprozess ab.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.title}
                            className="group p-7 rounded-3xl bg-white hover:shadow-xl hover:-translate-y-1 transition-all"
                            style={{ borderLeft: `5px solid ${i % 2 === 0 ? "#E7343F" : "#F9B000"}` }}
                            data-testid={`feature-detail-${f.title}`}
                        >
                            <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white transition-transform group-hover:scale-110" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                                <f.icon size={22} strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading text-xl font-bold text-warm-strong mt-5 mb-2">{f.title}</h3>
                            <p className="text-warm leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                <div className="relative overflow-hidden rounded-3xl p-12 text-center" style={{ background: "var(--gradient-primary)" }}>
                    <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
                        backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }} />
                    <div className="relative">
                        <h2 className="font-heading text-3xl lg:text-5xl font-bold text-white tracking-tighter">
                            Probiere es <span className="text-white/70">kostenlos</span> aus.
                        </h2>
                        <p className="mt-4 text-white/90 text-lg">Erste Bewerbung gratis. Keine Kreditkarte.</p>
                        <Link to="/registrieren" className="mt-7 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-warm-strong font-semibold hover:-translate-y-0.5 transition-transform shadow-xl">
                            Jetzt starten <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
